/**
 * StaffMFARecovery.js
 *
 * Staff MFA recovery page.
 * Route: /admin/mfa-recovery
 *
 * Phase A (no token): Identity verification form (email + password)
 *   -> POST /v1/staff/auth/mfa-recovery/request
 *   -> Anti-enumeration: always positive message
 *
 * Phase B (token in URL): Confirm recovery
 *   -> On mount: confirm via POST /v1/staff/auth/mfa-recovery/confirm
 *   -> On success: redirect to /admin/login (MFA re-enrollment required)
 *
 * Security:
 *   - Password required to request recovery (identity verification)
 *   - Token expires in 1 hour
 *   - All sessions revoked server-side on confirm
 *   - MFA cleared -- re-enrollment forced on next login
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://api.visinvoke.com';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #080B12; color: #E2E8F0; font-family: 'DM Sans', sans-serif; }
  .mr-root { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 24px; background: #080B12; position: relative; overflow: hidden; }
  .mr-bg-grid { position: absolute; inset: 0; background-image: linear-gradient(rgba(201,168,76,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.04) 1px, transparent 1px); background-size: 60px 60px; pointer-events: none; }
  .mr-card { position: relative; z-index: 1; width: 100%; max-width: 440px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 20px; padding: 48px 40px; backdrop-filter: blur(20px); animation: mr-fadeup 0.5s ease both; }
  @keyframes mr-fadeup { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
  .mr-logo { display: flex; align-items: center; gap: 12px; margin-bottom: 32px; }
  .mr-logo-mark { width: 36px; height: 36px; background: linear-gradient(135deg,#C9A84C,#8B0000); border-radius: 9px; display: flex; align-items: center; justify-content: center; font-family: 'Syne',sans-serif; font-weight: 800; font-size: 16px; color: #fff; flex-shrink: 0; }
  .mr-logo-text { font-family: 'Syne',sans-serif; font-weight: 700; font-size: 16px; color: #fff; }
  .mr-logo-sub { font-size: 10px; color: #C9A84C; letter-spacing: 2px; text-transform: uppercase; margin-top: 1px; }
  .mr-title { font-family: 'Syne',sans-serif; font-size: 22px; font-weight: 700; color: #fff; margin-bottom: 6px; }
  .mr-sub { font-size: 13px; color: rgba(255,255,255,0.4); line-height: 1.6; margin-bottom: 28px; }
  .mr-label { display: block; font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.4); letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 6px; margin-top: 16px; }
  .mr-input { width: 100%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; padding: 12px 16px; font-size: 14px; color: #fff; font-family: 'DM Sans',sans-serif; outline: none; transition: border-color 0.2s; }
  .mr-input:focus { border-color: rgba(201,168,76,0.5); }
  .mr-btn { width: 100%; padding: 13px; background: linear-gradient(135deg,#C9A84C,#A0832E); border: none; border-radius: 10px; font-size: 14px; font-weight: 700; color: #080B12; cursor: pointer; font-family: 'Syne',sans-serif; margin-top: 20px; transition: opacity 0.2s; }
  .mr-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .mr-err { font-size: 12px; color: #FCA5A5; margin-top: 10px; padding: 10px 14px; background: rgba(239,68,68,0.08); border-radius: 8px; border: 1px solid rgba(239,68,68,0.2); }
  .mr-ok { font-size: 12px; color: #4ADE80; margin-top: 10px; padding: 10px 14px; background: rgba(74,222,128,0.08); border-radius: 8px; border: 1px solid rgba(74,222,128,0.2); }
  .mr-warn { font-size: 12px; color: #FCD34D; margin-top: 10px; padding: 10px 14px; background: rgba(252,211,77,0.06); border-radius: 8px; border: 1px solid rgba(252,211,77,0.2); line-height: 1.6; }
  .mr-back { display: block; text-align: center; margin-top: 20px; font-size: 12px; color: rgba(201,168,76,0.7); text-decoration: none; }
  .mr-back:hover { color: #C9A84C; }
  .mr-loader { display: flex; align-items: center; justify-content: center; padding: 40px; gap: 10px; color: rgba(255,255,255,0.3); font-size: 13px; }
`;

function Logo() {
  return (
    <div className="mr-logo">
      <div className="mr-logo-mark">VI</div>
      <div>
        <div className="mr-logo-text">VISINVOKE</div>
        <div className="mr-logo-sub">Staff Access</div>
      </div>
    </div>
  );
}

// Phase A: Identity verification + recovery request
function RequestPhase() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [sent, setSent]         = useState(false);
  const [err, setErr]           = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setErr('Email and password are required.'); return; }
    setLoading(true); setErr('');
    try {
      await fetch(`${API}/v1/staff/auth/mfa-recovery/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      setSent(true);
    } catch {
      setErr('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) return (
    <>
      <Logo />
      <div className="mr-title">Check Your Email</div>
      <div className="mr-sub">If your credentials were valid, a recovery link has been sent to your email address. The link expires in 1 hour.</div>
      <div className="mr-ok">Recovery email sent if credentials matched.</div>
      <div className="mr-warn" style={{ marginTop: 12 }}>
        <strong>Security notice:</strong> After completing recovery, your MFA will be reset and you will be required to re-enroll on your next login. All active sessions will be revoked.
      </div>
      <Link to="/admin/login" className="mr-back">Back to login</Link>
    </>
  );

  return (
    <>
      <Logo />
      <div className="mr-title">MFA Recovery</div>
      <div className="mr-sub">Lost access to your authenticator? Verify your identity to request MFA recovery. Your password is required to confirm identity.</div>
      <div className="mr-warn">
        <strong>Security warning:</strong> MFA recovery will clear your current MFA setup and revoke all active sessions. You will need to re-enroll MFA on next login.
      </div>
      <form onSubmit={handleSubmit}>
        <label className="mr-label">Staff Email Address</label>
        <input className="mr-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="staff@visinvoke.com" autoFocus required/>
        <label className="mr-label">Current Password</label>
        <input className="mr-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Your current password" autoComplete="current-password" required/>
        {err && <div className="mr-err">{err}</div>}
        <button className="mr-btn" type="submit" disabled={loading}>{loading ? 'Verifying...' : 'Request MFA Recovery'}</button>
      </form>
      <Link to="/admin/login" className="mr-back">Back to login</Link>
    </>
  );
}

// Phase B: Confirm recovery with token
function ConfirmPhase({ token }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('confirming'); // confirming | success | error
  const [err, setErr]     = useState('');

  const confirm = useCallback(async () => {
    try {
      const r = await fetch(`${API}/v1/staff/auth/mfa-recovery/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const d = await r.json();
      if (r.ok) {
        setPhase('success');
      } else {
        setErr(d.error || 'Recovery failed. Please request a new recovery link.');
        setPhase('error');
      }
    } catch {
      setErr('Network error. Please try again.');
      setPhase('error');
    }
  }, [token]);

  useEffect(() => { confirm(); }, [confirm]);

  if (phase === 'confirming') return (
    <>
      <Logo />
      <div className="mr-loader">Processing MFA recovery...</div>
    </>
  );

  if (phase === 'error') return (
    <>
      <Logo />
      <div className="mr-title">Recovery Failed</div>
      <div className="mr-err" style={{ marginTop: 0 }}>{err}</div>
      <Link to="/admin/mfa-recovery" className="mr-back" style={{ marginTop: 16, display: 'block', textAlign: 'center', color: 'rgba(201,168,76,0.7)', fontSize: 12 }}>Request a new recovery link</Link>
      <Link to="/admin/login" className="mr-back">Back to login</Link>
    </>
  );

  return (
    <>
      <Logo />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(74,222,128,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
        </div>
        <div className="mr-title" style={{ marginBottom: 0 }}>MFA Reset</div>
      </div>
      <div className="mr-sub">Your MFA has been cleared and all sessions revoked.</div>
      <div className="mr-ok">MFA recovery complete.</div>
      <div className="mr-warn" style={{ marginTop: 12 }}>
        You will be required to set up MFA again when you log in. Keep your authenticator app ready.
      </div>
      <button className="mr-btn" onClick={() => navigate('/admin/login')}>Proceed to Login</button>
    </>
  );
}

export default function StaffMFARecovery() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  return (
    <>
      <style>{styles}</style>
      <div className="mr-root">
        <div className="mr-bg-grid"/>
        <div className="mr-card">
          {token ? <ConfirmPhase token={token}/> : <RequestPhase/>}
        </div>
      </div>
    </>
  );
}
